import React, { useEffect, useState, useCallback } from "react";
import { Inbox, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import DashShell from "@/components/dashboard/DashShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [{ key: "inbox", label: "Message Dox Inbox", icon: Inbox }];
const STATUSES = ["New", "In Progress", "Resolved"];
const TONE = { New: "bg-dox-orange/15 text-dox-orange", "In Progress": "bg-amber-500/15 text-amber-400", Resolved: "bg-emerald-500/15 text-emerald-400" };

export default function Admin() {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/contacts"); setContacts(data); }
    catch { toast.error("Could not load inbox."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}/status`, { status });
      setContacts((c) => c.map((x) => (x.id === id ? { ...x, status } : x)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed."); }
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    try {
      const { data } = await api.post(`/admin/contacts/${selected.id}/notes`, { note: note.trim() });
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), data] }));
      setContacts((c) => c.map((x) => (x.id === selected.id ? { ...x, notes: [...(x.notes || []), data] } : x)));
      setNote("");
      toast.success("Note added.");
    } catch { toast.error("Could not add note."); }
  };

  return (
    <DashShell nav={NAV} active="inbox" onNavigate={() => {}} title="Message Dox Inbox">
      <div className="mb-6 flex items-center gap-3">
        {STATUSES.map((s) => (
          <div key={s} className="glass rounded-xl px-4 py-2">
            <div className="text-xs text-white/40">{s}</div>
            <div className="font-display font-black text-xl">{contacts.filter((c) => c.status === s).length}</div>
          </div>
        ))}
      </div>

      <FieldCorrectionInsights />

      {contacts.length === 0 ? (
        <div className="text-center py-16 text-white/40">No messages yet.</div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-surface overflow-hidden">
          {contacts.map((c) => (
            <button
              key={c.id}
              data-testid={`contact-row-${c.id}`}
              onClick={() => setSelected(c)}
              className="w-full flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-dox-orange to-dox-purple flex items-center justify-center text-sm font-bold shrink-0">
                {c.name?.slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{c.name}</span>
                  <span className="font-mono text-xs text-white/30">{c.tracking_ref}</span>
                </div>
                <div className="text-sm text-white/50 truncate">{c.subject} — {c.message}</div>
              </div>
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${TONE[c.status]}`}>{c.status}</span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg bg-surface border-white/10 text-white max-h-[88vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <Mail size={18} className="text-dox-orange" /> {selected.subject}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-semibold">{selected.name}</div>
                    <div className="text-white/50">{selected.email}</div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="status-dropdown" className={`text-xs font-bold px-3 py-1.5 rounded-full ${TONE[selected.status]}`}>{selected.status} ▾</button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-elevated border-white/10 text-white">
                      {STATUSES.map((s) => (
                        <DropdownMenuItem key={s} data-testid={`set-status-${s.replace(/\s/g, '-')}`} onClick={() => setStatus(selected.id, s)} className="cursor-pointer focus:bg-white/10 focus:text-white">{s}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-sm text-white/70 leading-relaxed">{selected.message}</div>
                <div className="text-xs text-white/30 font-mono">Ref {selected.tracking_ref} · {selected.ip} · {new Date(selected.created_at).toLocaleString()}</div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2">Internal notes</div>
                  <div className="space-y-2 mb-3">
                    {(selected.notes || []).length === 0 && <p className="text-white/30 text-sm">No notes yet.</p>}
                    {(selected.notes || []).map((n, i) => (
                      <div key={i} className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm">
                        <div className="text-white/70">{n.note}</div>
                        <div className="text-[10px] text-white/30 mt-1 font-mono">{n.by} · {new Date(n.at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-dox-orange" />
                    <button data-testid="note-add" onClick={addNote} className="rounded-xl bg-dox-orange px-4 text-sm font-bold hover:bg-dox-orangeHover transition-colors">Add</button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashShell>
  );
}



function FieldCorrectionInsights() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/insights/field-corrections");
        if (!cancel) setData(data);
      } catch { /* silent — tile just won't render */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="mb-6 h-40 rounded-2xl bg-white/5 animate-pulse" />;
  if (!data) return null;

  return (
    <div className="mb-8 rounded-2xl border border-white/10 bg-surface p-5 sm:p-6" data-testid="field-correction-insights">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-dox-blue/15 border border-dox-blue/30 flex items-center justify-center">
            <Sparkles size={18} className="text-dox-lightBlue" />
          </div>
          <div>
            <div className="font-display font-black text-lg">Most-corrected AI fields</div>
            <div className="text-xs text-white/50">Retune these extractor patterns first.</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="glass rounded-lg px-3 py-1.5">
            <span className="text-white/50">Corrections</span>
            <span className="font-bold text-white ml-1.5" data-testid="fci-total">{data.total_corrections}</span>
          </div>
          <div className="glass rounded-lg px-3 py-1.5">
            <span className="text-white/50">Docs edited</span>
            <span className="font-bold text-white ml-1.5" data-testid="fci-docs">{data.documents_edited}</span>
          </div>
        </div>
      </div>

      {data.rows.length === 0 ? (
        <div className="text-sm text-white/45 text-center py-6">
          No corrections yet — the AI extractor is holding up. 🎯
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-white/8">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-2.5">Field</th>
                <th className="text-left px-4 py-2.5">Document type</th>
                <th className="text-right px-4 py-2.5">Corrections</th>
                <th className="text-right px-4 py-2.5">Documents</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.slice(0, 10).map((r, i) => (
                <tr key={i} className="border-t border-white/5" data-testid={`fci-row-${r.field}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-white/85">{r.field}</td>
                  <td className="px-4 py-2.5 text-white/70">{r.document_type && r.document_type !== "unknown" ? r.document_type : <span className="text-white/35">— Not classified —</span>}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-white">{r.corrections}</td>
                  <td className="px-4 py-2.5 text-right text-white/60">{r.documents_affected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
